import React from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuRadioGroup
} from '../../components/dropdown-menu'
import { cn } from '../../lib/utils'

export interface UIDropdownMenuProps extends React.ComponentProps<typeof DropdownMenu> {}

export interface UIDropdownMenuContentProps extends React.ComponentProps<typeof DropdownMenuContent> {
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'elevated' | 'minimal'
}

export interface UIDropdownMenuItemProps extends Omit<React.ComponentProps<typeof DropdownMenuItem>, 'variant'> {
  variant?: 'default' | 'destructive' | 'success'
  size?: 'sm' | 'md'
}

export interface UIDropdownMenuLabelProps extends React.ComponentProps<typeof DropdownMenuLabel> {
  variant?: 'default' | 'section'
}

/**
 * Enhanced DropdownMenu component following design system best practices
 * Provides consistent dropdown menu styling and behavior
 */
export const UIDropdownMenu = ({ ...props }: UIDropdownMenuProps) => {
  return (
    <DropdownMenu
      {...props}
    />
  )
}

export const UIDropdownMenuContent = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuContent>,
  UIDropdownMenuContentProps
>(({ className, size = 'md', variant = 'default', ...props }, ref) => {
  return (
    <DropdownMenuContent
      ref={ref}
      className={cn(
        // Size styles
        {
          'min-w-32 p-1': size === 'sm',
          'min-w-40 p-1.5': size === 'md',
          'min-w-48 p-2': size === 'lg',
        },

        // Variant styles
        {
          'border border-border shadow-md': variant === 'default',
          'border border-border shadow-lg': variant === 'elevated',
          'border-none shadow-sm': variant === 'minimal',
        },

        className
      )}
      {...props}
    />
  )
})

export const UIDropdownMenuItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuItem>,
  UIDropdownMenuItemProps
>(({ className, variant = 'default', size = 'md', ...props }, ref) => {
  return (
    <DropdownMenuItem
      ref={ref}
      className={cn(
        // Base styles
        'transition-colors focus:outline-none',

        // Size styles
        {
          'px-2 py-1.5 text-sm': size === 'sm',
          'px-3 py-2 text-sm': size === 'md',
        },

        // Variant styles
        {
          'text-foreground hover:bg-accent focus:bg-accent': variant === 'default',
          'text-destructive hover:bg-destructive/10 focus:bg-destructive/10': variant === 'destructive',
          'text-green-600 hover:bg-green-50 focus:bg-green-50 dark:text-green-400 dark:hover:bg-green-950 dark:focus:bg-green-950': variant === 'success',
        },

        className
      )}
      {...props}
    />
  )
})

export const UIDropdownMenuLabel = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuLabel>,
  UIDropdownMenuLabelProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <DropdownMenuLabel
      ref={ref}
      className={cn(
        {
          'px-3 py-2 text-sm font-medium': variant === 'default',
          'px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground': variant === 'section',
        },
        className
      )}
      {...props}
    />
  )
})

// Re-export other dropdown menu components with consistent naming
export const UIDropdownMenuTrigger = DropdownMenuTrigger
export const UIDropdownMenuGroup = DropdownMenuGroup
export const UIDropdownMenuPortal = DropdownMenuPortal
export const UIDropdownMenuSeparator = DropdownMenuSeparator
export const UIDropdownMenuShortcut = DropdownMenuShortcut
export const UIDropdownMenuSub = DropdownMenuSub
export const UIDropdownMenuSubContent = DropdownMenuSubContent
export const UIDropdownMenuSubTrigger = DropdownMenuSubTrigger
export const UIDropdownMenuCheckboxItem = DropdownMenuCheckboxItem
export const UIDropdownMenuRadioItem = DropdownMenuRadioItem
export const UIDropdownMenuRadioGroup = DropdownMenuRadioGroup

UIDropdownMenu.displayName = "UIDropdownMenu"
UIDropdownMenuContent.displayName = "UIDropdownMenuContent"
UIDropdownMenuItem.displayName = "UIDropdownMenuItem"
UIDropdownMenuLabel.displayName = "UIDropdownMenuLabel"